import type { Container, Document, DocumentType } from "@/types/container";
import type { CustomFolder, DocumentFile, UnmappedFile } from "@/types/documents";
import { containerService } from "./mock-containers";
import { customFolderService } from "./mock-custom-folders";

// Helper per generare fileUrl basato sul nome file
const getFileUrl = (name: string): string => {
    const isPdf = name.toLowerCase().endsWith(".pdf");
    if (isPdf) {
        return "/document-review-test.pdf"; // Usa il PDF di test esistente
    }
    return `/mock-files/${encodeURIComponent(name)}`;
};

// Helper per determinare mimeType
const getMimeType = (name: string): string => {
    const ext = name.split(".").pop()?.toLowerCase();
    const mimeTypes: Record<string, string> = {
        pdf: "application/pdf",
        doc: "application/msword",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        xls: "application/vnd.ms-excel",
        xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
        zip: "application/zip",
        txt: "text/plain",
    };
    return mimeTypes[ext || ""] || "application/octet-stream";
};

// Helper per generare fileSize realistico
const getFileSize = (name: string): number => {
    const isPdf = name.toLowerCase().endsWith(".pdf");
    if (isPdf) {
        return 1024 * 1250; // ~1.25 MB per PDF
    }
    return 1024 * (500 + Math.floor(Math.random() * 1000)); // 500KB - 1.5MB
};

// Mock data per documents (estesi con metadati)
const mockDocuments: DocumentFile[] = [
    // Container 1 - TCLU1234567
    {
        id: "1",
        containerId: "1",
        type: "bill_of_lading",
        name: "Bill of Lading - TCLU1234567.pdf",
        uploadedAt: new Date("2024-01-22"),
        extractedData: {
            "B/L No": "TCLU1234567",
            SCAC: "TCLU",
            Vessel: "MSC LORETO",
            "Port of Loading": "New York, USA",
            Shipper: "TechCorp Solutions LLC",
            Consignee: "Global Partners Corp",
        },
        missingFields: [],
        status: "Completo",
        fileUrl: getFileUrl("Bill of Lading - TCLU1234567.pdf"),
        fileSize: getFileSize("Bill of Lading - TCLU1234567.pdf"),
        mimeType: getMimeType("Bill of Lading - TCLU1234567.pdf"),
    },
    {
        id: "2",
        containerId: "1",
        type: "invoice",
        name: "Commercial Invoice - TCLU1234567.pdf",
        uploadedAt: new Date("2024-01-23"),
        extractedData: {
            "Invoice No": "INV-2024-001",
            "Invoice Date": "2024-01-20",
            "Total Amount": "$125,000.00",
            "Bank Details": "Chase Bank, New York",
            "Unit Price": "$1,250.00",
        },
        missingFields: [],
        status: "Completo",
        fileUrl: getFileUrl("Commercial Invoice - TCLU1234567.pdf"),
        fileSize: getFileSize("Commercial Invoice - TCLU1234567.pdf"),
        mimeType: getMimeType("Commercial Invoice - TCLU1234567.pdf"),
    },
    {
        id: "3",
        containerId: "1",
        type: "packing_list",
        name: "Packing List - TCLU1234567.pdf",
        uploadedAt: new Date("2024-01-24"),
        extractedData: {
            "Packing List": "PL-2024-001",
            "Total Packages": "50",
            Weight: "25,000 kg",
            Dimensions: "40x8x8 ft",
        },
        missingFields: [],
        status: "Completo",
        fileUrl: getFileUrl("Packing List - TCLU1234567.pdf"),
        fileSize: getFileSize("Packing List - TCLU1234567.pdf"),
        mimeType: getMimeType("Packing List - TCLU1234567.pdf"),
    },
    {
        id: "3a",
        containerId: "1",
        type: "invoice",
        name: "Invoice Details - TCLU1234567.xlsx",
        uploadedAt: new Date("2024-01-25"),
        extractedData: {
            "Invoice No": "INV-2024-001-DETAILS",
            "Total Amount": "$125,000.00",
        },
        missingFields: [],
        status: "Completo",
        fileUrl: getFileUrl("Invoice Details - TCLU1234567.xlsx"),
        fileSize: getFileSize("Invoice Details - TCLU1234567.xlsx"),
        mimeType: getMimeType("Invoice Details - TCLU1234567.xlsx"),
    },
    {
        id: "3b",
        containerId: "1",
        type: "packing_list",
        name: "Container Photos - TCLU1234567.jpg",
        uploadedAt: new Date("2024-01-26"),
        extractedData: {
            "Photo Type": "Container Seal",
            Location: "Port of Loading",
        },
        missingFields: [],
        status: "Completo",
        fileUrl: getFileUrl("Container Photos - TCLU1234567.jpg"),
        fileSize: getFileSize("Container Photos - TCLU1234567.jpg"),
        mimeType: getMimeType("Container Photos - TCLU1234567.jpg"),
    },

    // Container 2 - TCLU1234568
    {
        id: "4",
        containerId: "2",
        type: "bill_of_lading",
        name: "Bill of Lading - TCLU1234568.pdf",
        uploadedAt: new Date("2024-01-26"),
        extractedData: {
            "B/L No": "TCLU1234568",
            SCAC: "TCLU",
            Vessel: "MSC LORETO",
            "Port of Loading": "New York, USA",
            Shipper: "TechCorp Solutions LLC",
            Consignee: "Green Future NGO",
        },
        missingFields: [],
        status: "Completo",
        fileUrl: getFileUrl("Bill of Lading - TCLU1234568.pdf"),
        fileSize: getFileSize("Bill of Lading - TCLU1234568.pdf"),
        mimeType: getMimeType("Bill of Lading - TCLU1234568.pdf"),
    },
    {
        id: "5",
        containerId: "2",
        type: "phytosanitary_certificate",
        name: "Phytosanitary Certificate - TCLU1234568.pdf",
        uploadedAt: new Date("2024-01-27"),
        extractedData: {
            "Phytosanitary Certificate": "PHY-2024-001",
            KEPHIS: "KEPHIS-001",
            "Plant Health": "Certified",
            "Botanical name": "Rosa canina",
            "IPPC Stamp": "Present",
            Conformity: "Yes",
        },
        missingFields: [],
        status: "Completo",
        fileUrl: getFileUrl("Phytosanitary Certificate - TCLU1234568.pdf"),
        fileSize: getFileSize("Phytosanitary Certificate - TCLU1234568.pdf"),
        mimeType: getMimeType("Phytosanitary Certificate - TCLU1234568.pdf"),
    },
    {
        id: "5a",
        containerId: "2",
        type: "phytosanitary_certificate",
        name: "Plant Inspection Report - TCLU1234568.docx",
        uploadedAt: new Date("2024-01-28"),
        extractedData: {
            "Inspection Date": "2024-01-27",
            Inspector: "Dr. Jane Smith",
        },
        missingFields: [],
        status: "Completo",
        fileUrl: getFileUrl("Plant Inspection Report - TCLU1234568.docx"),
        fileSize: getFileSize("Plant Inspection Report - TCLU1234568.docx"),
        mimeType: getMimeType("Plant Inspection Report - TCLU1234568.docx"),
    },
    {
        id: "5b",
        containerId: "2",
        type: "unknown",
        name: "Product Images - TCLU1234568.png",
        uploadedAt: new Date("2024-01-29"),
        extractedData: {
            "Image Type": "Product Photos",
        },
        missingFields: [],
        status: "Completo",
        fileUrl: getFileUrl("Product Images - TCLU1234568.png"),
        fileSize: getFileSize("Product Images - TCLU1234568.png"),
        mimeType: getMimeType("Product Images - TCLU1234568.png"),
    },
    {
        id: "6",
        containerId: "2",
        type: "invoice",
        name: "Commercial Invoice - TCLU1234568.pdf",
        uploadedAt: new Date("2024-01-28"),
        extractedData: {
            "Invoice No": "INV-2024-002",
            "Invoice Date": "2024-01-25",
            "Total Amount": "$85,000.00",
            "Bank Details": "Chase Bank, New York",
        },
        missingFields: ["Unit Price"],
        status: "Pending",
        fileUrl: getFileUrl("Commercial Invoice - TCLU1234568.pdf"),
        fileSize: getFileSize("Commercial Invoice - TCLU1234568.pdf"),
        mimeType: getMimeType("Commercial Invoice - TCLU1234568.pdf"),
    },

    // Container 3 - IHDE1234567
    {
        id: "7",
        containerId: "3",
        type: "movement_certificate",
        name: "EUR.1 Movement Certificate - IHDE1234567.pdf",
        uploadedAt: new Date("2024-01-12"),
        extractedData: {
            "EUR.1": "EUR-2024-001",
            "Movement Certificate": "MC-2024-001",
            "Customs office": "Berlin Customs",
            Exporter: "Innovation Hub Partnership",
            "Box 1": "Germany",
            "Box 2": "France",
            "Box 3": "Industrial Equipment",
        },
        missingFields: [],
        status: "Completo",
        fileUrl: getFileUrl("EUR.1 Movement Certificate - IHDE1234567.pdf"),
        fileSize: getFileSize("EUR.1 Movement Certificate - IHDE1234567.pdf"),
        mimeType: getMimeType("EUR.1 Movement Certificate - IHDE1234567.pdf"),
    },
    {
        id: "8",
        containerId: "3",
        type: "invoice",
        name: "Commercial Invoice - IHDE1234567.pdf",
        uploadedAt: new Date("2024-01-13"),
        extractedData: {
            "Invoice No": "INV-2024-003",
            "Invoice Date": "2024-01-10",
            "Total Amount": "€95,000.00",
            "Bank Details": "Deutsche Bank, Berlin",
            "Unit Price": "€950.00",
        },
        missingFields: [],
        status: "Completo",
        fileUrl: getFileUrl("Commercial Invoice - IHDE1234567.pdf"),
        fileSize: getFileSize("Commercial Invoice - IHDE1234567.pdf"),
        mimeType: getMimeType("Commercial Invoice - IHDE1234567.pdf"),
    },

    // Container 4 - SPCA1234567
    {
        id: "9",
        containerId: "4",
        type: "coo",
        name: "Certificate of Origin - SPCA1234567.pdf",
        uploadedAt: new Date("2024-01-29"),
        extractedData: {
            COO: "COO-2024-001",
            "CERTIFICATE OF ORIGIN": "CO-2024-001",
            "No. OC": "OC-2024-001",
        },
        missingFields: ["Exporter", "Importer", "Country of Origin"],
        status: "Error",
        fileUrl: getFileUrl("Certificate of Origin - SPCA1234567.pdf"),
        fileSize: getFileSize("Certificate of Origin - SPCA1234567.pdf"),
        mimeType: getMimeType("Certificate of Origin - SPCA1234567.pdf"),
    },
    {
        id: "10",
        containerId: "4",
        type: "fumigation_certificate",
        name: "Fumigation Certificate - SPCA1234567.pdf",
        uploadedAt: new Date("2024-01-30"),
        extractedData: {
            Fumigation: "FUM-2024-001",
            "Treatment Date": "2024-01-28",
            "Chemical Used": "Methyl Bromide",
        },
        missingFields: [],
        status: "Completo",
        fileUrl: getFileUrl("Fumigation Certificate - SPCA1234567.pdf"),
        fileSize: getFileSize("Fumigation Certificate - SPCA1234567.pdf"),
        mimeType: getMimeType("Fumigation Certificate - SPCA1234567.pdf"),
    },

    // Container 5 - TCLU1234569
    {
        id: "11",
        containerId: "5",
        type: "bill_of_lading",
        name: "Bill of Lading - TCLU1234569.pdf",
        uploadedAt: new Date("2024-01-16"),
        extractedData: {
            "B/L No": "TCLU1234569",
            SCAC: "TCLU",
            Vessel: "MSC LORETO",
            "Port of Loading": "New York, USA",
            Shipper: "TechCorp Solutions LLC",
            Consignee: "Global Partners Corp",
        },
        missingFields: [],
        status: "Completo",
        fileUrl: getFileUrl("Bill of Lading - TCLU1234569.pdf"),
        fileSize: getFileSize("Bill of Lading - TCLU1234569.pdf"),
        mimeType: getMimeType("Bill of Lading - TCLU1234569.pdf"),
    },
    {
        id: "12",
        containerId: "5",
        type: "sad_icm",
        name: "SAD/ICM - TCLU1234569.pdf",
        uploadedAt: new Date("2024-01-17"),
        extractedData: {
            "SINGLE ADMINISTRATIVE DOCUMENT(SAD)-ICMS": "SAD-2024-001",
            "Customs Office": "New York Customs",
            "Declaration Date": "2024-01-16",
        },
        missingFields: [],
        status: "Completo",
        fileUrl: getFileUrl("SAD/ICM - TCLU1234569.pdf"),
        fileSize: getFileSize("SAD/ICM - TCLU1234569.pdf"),
        mimeType: getMimeType("SAD/ICM - TCLU1234569.pdf"),
    },

    // Container 6 - IHDE1234568
    {
        id: "13",
        containerId: "6",
        type: "unknown",
        name: "Document - IHDE1234568.pdf",
        uploadedAt: new Date("2024-01-23"),
        extractedData: {
            "Document Type": "Unknown",
            Content: "Scanned document",
        },
        missingFields: ["Document Type", "Key Information"],
        status: "Error",
        fileUrl: getFileUrl("Document - IHDE1234568.pdf"),
        fileSize: getFileSize("Document - IHDE1234568.pdf"),
        mimeType: getMimeType("Document - IHDE1234568.pdf"),
    },

    // Container 7 - SPCA1234568
    {
        id: "14",
        containerId: "7",
        type: "invoice",
        name: "Commercial Invoice - SPCA1234568.pdf",
        uploadedAt: new Date("2024-01-31"),
        extractedData: {
            "Invoice No": "INV-2024-004",
            "Invoice Date": "2024-01-30",
            "Total Amount": "C$30,000.00",
        },
        missingFields: ["Bank Details", "Unit Price"],
        status: "Pending",
        fileUrl: getFileUrl("Commercial Invoice - SPCA1234568.pdf"),
        fileSize: getFileSize("Commercial Invoice - SPCA1234568.pdf"),
        mimeType: getMimeType("Commercial Invoice - SPCA1234568.pdf"),
    },

    // Container 8 - TCLU1234570
    {
        id: "15",
        containerId: "8",
        type: "bill_of_lading",
        name: "Bill of Lading - TCLU1234570.pdf",
        uploadedAt: new Date("2024-01-06"),
        extractedData: {
            "B/L No": "TCLU1234570",
            SCAC: "TCLU",
            Vessel: "MSC LORETO",
            "Port of Loading": "New York, USA",
            Shipper: "TechCorp Solutions LLC",
            Consignee: "Health Foundation NGO",
        },
        missingFields: [],
        status: "Completo",
        fileUrl: getFileUrl("Bill of Lading - TCLU1234570.pdf"),
        fileSize: getFileSize("Bill of Lading - TCLU1234570.pdf"),
        mimeType: getMimeType("Bill of Lading - TCLU1234570.pdf"),
    },
    {
        id: "16",
        containerId: "8",
        type: "invoice",
        name: "Commercial Invoice - TCLU1234570.pdf",
        uploadedAt: new Date("2024-01-07"),
        extractedData: {
            "Invoice No": "INV-2024-005",
            "Invoice Date": "2024-01-05",
            "Total Amount": "$150,000.00",
            "Bank Details": "Chase Bank, New York",
            "Unit Price": "$1,500.00",
        },
        missingFields: [],
        status: "Completo",
        fileUrl: getFileUrl("Commercial Invoice - TCLU1234570.pdf"),
        fileSize: getFileSize("Commercial Invoice - TCLU1234570.pdf"),
        mimeType: getMimeType("Commercial Invoice - TCLU1234570.pdf"),
    },
];

// Mock data per file unmapped (non associati a container)
const mockUnmappedFiles: UnmappedFile[] = [
    {
        id: "unmapped-1",
        name: "Unmapped Document 1.pdf",
        type: "bill_of_lading",
        uploadedAt: new Date("2024-02-01"),
        fileUrl: getFileUrl("Unmapped Document 1.pdf"),
        fileSize: getFileSize("Unmapped Document 1.pdf"),
        mimeType: getMimeType("Unmapped Document 1.pdf"),
        extractedData: {
            "B/L No": "UNMAPPED-001",
            SCAC: "UNMP",
        },
        missingFields: ["Vessel", "Port of Loading"],
        status: "Pending",
    },
    {
        id: "unmapped-2",
        name: "Unmapped Invoice.xlsx",
        type: "invoice",
        uploadedAt: new Date("2024-02-02"),
        fileUrl: getFileUrl("Unmapped Invoice.xlsx"),
        fileSize: getFileSize("Unmapped Invoice.xlsx"),
        mimeType: getMimeType("Unmapped Invoice.xlsx"),
        extractedData: {
            "Invoice No": "INV-UNMAPPED-001",
        },
        missingFields: ["Invoice Date", "Total Amount"],
        status: "Pending",
    },
    {
        id: "unmapped-3",
        name: "Unmapped Packing List.pdf",
        type: "packing_list",
        uploadedAt: new Date("2024-02-03"),
        fileUrl: getFileUrl("Unmapped Packing List.pdf"),
        fileSize: getFileSize("Unmapped Packing List.pdf"),
        mimeType: getMimeType("Unmapped Packing List.pdf"),
        extractedData: {
            "Packing List": "PL-UNMAPPED-001",
        },
        missingFields: ["Total Packages", "Weight"],
        status: "Pending",
    },
    {
        id: "unmapped-4",
        name: "Unmapped Certificate.doc",
        type: "coo",
        uploadedAt: new Date("2024-02-04"),
        fileUrl: getFileUrl("Unmapped Certificate.doc"),
        fileSize: getFileSize("Unmapped Certificate.doc"),
        mimeType: getMimeType("Unmapped Certificate.doc"),
        extractedData: {},
        missingFields: ["Exporter", "Importer"],
        status: "Error",
    },
    {
        id: "unmapped-5",
        name: "Unmapped Document 2.pdf",
        type: "unknown",
        uploadedAt: new Date("2024-02-05"),
        fileUrl: getFileUrl("Unmapped Document 2.pdf"),
        fileSize: getFileSize("Unmapped Document 2.pdf"),
        mimeType: getMimeType("Unmapped Document 2.pdf"),
        extractedData: {},
        missingFields: ["Document Type"],
        status: "Error",
    },
    {
        id: "unmapped-6",
        name: "Unmapped Shipping Manifest.docx",
        type: "packing_list",
        uploadedAt: new Date("2024-02-06"),
        fileUrl: getFileUrl("Unmapped Shipping Manifest.docx"),
        fileSize: getFileSize("Unmapped Shipping Manifest.docx"),
        mimeType: getMimeType("Unmapped Shipping Manifest.docx"),
        extractedData: {
            "Manifest No": "MAN-UNMAPPED-001",
        },
        missingFields: ["Total Packages", "Weight"],
        status: "Pending",
    },
    {
        id: "unmapped-7",
        name: "Unmapped Container Photo.jpg",
        type: "unknown",
        uploadedAt: new Date("2024-02-07"),
        fileUrl: getFileUrl("Unmapped Container Photo.jpg"),
        fileSize: getFileSize("Unmapped Container Photo.jpg"),
        mimeType: getMimeType("Unmapped Container Photo.jpg"),
        extractedData: {},
        missingFields: ["Document Type"],
        status: "Pending",
    },
    {
        id: "unmapped-8",
        name: "Unmapped Certificate Archive.zip",
        type: "coo",
        uploadedAt: new Date("2024-02-08"),
        fileUrl: getFileUrl("Unmapped Certificate Archive.zip"),
        fileSize: getFileSize("Unmapped Certificate Archive.zip"),
        mimeType: getMimeType("Unmapped Certificate Archive.zip"),
        extractedData: {},
        missingFields: ["Exporter", "Importer"],
        status: "Error",
    },
];

// In-memory storage
let documents: DocumentFile[] = [...mockDocuments];
let unmappedFiles: UnmappedFile[] = [...mockUnmappedFiles];

// Helper functions per CRUD operations
export const documentService = {
    // Get all documents
    getAll: (): Document[] => {
        return [...documents];
    },

    // Get document by ID
    getById: (id: string): Document | undefined => {
        return documents.find((document) => document.id === id);
    },

    // Get documents by container ID
    getByContainerId: (containerId: string): Document[] => {
        return documents.filter((document) => document.containerId === containerId);
    },

    // Create new document
    create: (data: Omit<Document, "id" | "uploadedAt">): DocumentFile => {
        const newDocument: DocumentFile = {
            id: (documents.length + 1).toString(),
            ...data,
            uploadedAt: new Date(),
            // Generate file metadata if not provided
            fileUrl: getFileUrl(data.name),
            fileSize: getFileSize(data.name),
            mimeType: getMimeType(data.name),
        };
        documents.push(newDocument);
        return newDocument;
    },

    // Update document
    update: (id: string, data: Partial<Omit<Document, "id" | "uploadedAt">>): Document | null => {
        const index = documents.findIndex((document) => document.id === id);
        if (index === -1) return null;

        documents[index] = {
            ...documents[index],
            ...data,
        };
        return documents[index];
    },

    // Delete document
    delete: (id: string): boolean => {
        const index = documents.findIndex((document) => document.id === id);
        if (index === -1) return false;

        documents.splice(index, 1);
        return true;
    },

    // Get document type display name
    // Note: This function should be called from a component that has access to i18n
    // For now, we return the type key and translation should happen in components
    getDocumentTypeDisplayName: (type: DocumentType): string => {
        // Return type key - components will translate using useI18n hook
        return type;
    },

    // Get document type indicators
    getDocumentTypeIndicators: (type: DocumentType): string[] => {
        const indicatorsMap: Record<DocumentType, string[]> = {
            bill_of_lading: ["B/L No", "SCAC", "Vessel", "Port of Loading", "Shipper", "Consignee"],
            invoice: ["Invoice No", "Invoice Date", "Total Amount", "Bank Details", "Unit Price"],
            phytosanitary_certificate: ["Phytosanitary Certificate", "KEPHIS", "Plant Health", "Botanical name", "IPPC Stamp", "Conformity"],
            movement_certificate: ["EUR.1", "Movement Certificate", "Customs office", "Exporter", "Box 1-12"],
            packing_list: ["Packing List", "Total Packages", "Weight", "Dimensions"],
            fumigation_certificate: ["Fumigation", "Treatment Date", "Chemical Used"],
            sad_icm: ["SINGLE ADMINISTRATIVE DOCUMENT(SAD)-ICMS", "Customs Office", "Declaration Date"],
            coo: ["COO", "CERTIFICATE OF ORIGIN", "No. OC", "Exporter", "Importer", "Country of Origin"],
            unknown: ["Document Type", "Key Information"],
        };
        return indicatorsMap[type] || [];
    },

    // Get unmapped files (file senza containerId)
    getUnmappedFiles: (): UnmappedFile[] => {
        return [...unmappedFiles];
    },

    // Associate file to container (rimuove da unmapped e crea documento associato)
    associateToContainer: (fileId: string, containerId: string): DocumentFile | null => {
        const fileIndex = unmappedFiles.findIndex((f) => f.id === fileId);
        if (fileIndex === -1) return null;

        const file = unmappedFiles[fileIndex];
        const newDocument: DocumentFile = {
            id: file.id,
            containerId,
            type: file.type,
            name: file.name,
            uploadedAt: file.uploadedAt,
            extractedData: file.extractedData || {},
            missingFields: file.missingFields || [],
            status: file.status,
            fileUrl: file.fileUrl,
            fileSize: file.fileSize,
            mimeType: file.mimeType,
        };

        // Rimuovi da unmapped
        unmappedFiles.splice(fileIndex, 1);
        // Aggiungi a documents
        documents.push(newDocument);
        return newDocument;
    },

    // Bulk associate files to container
    bulkAssociateToContainer: (fileIds: string[], containerId: string): DocumentFile[] => {
        const associated: DocumentFile[] = [];
        fileIds.forEach((fileId) => {
            const doc = documentService.associateToContainer(fileId, containerId);
            if (doc) associated.push(doc);
        });
        return associated;
    },

    // Rename document
    rename: (id: string, newName: string): DocumentFile | null => {
        const index = documents.findIndex((doc) => doc.id === id);
        if (index === -1) return null;

        documents[index].name = newName;
        return documents[index];
    },

    // Move document to different container
    move: (id: string, targetContainerId: string): DocumentFile | null => {
        const index = documents.findIndex((doc) => doc.id === id);
        if (index === -1) return null;

        documents[index].containerId = targetContainerId;
        return documents[index];
    },

    // Share document (placeholder)
    share: (id: string, options: { email?: string; permission: "view" | "edit" | "comment"; expiresAt?: Date }): boolean => {
        // Placeholder - in un'implementazione reale, questo creerebbe un record di condivisione
        console.log("Share document", id, options);
        return true;
    },

    // Global search (cerca in documenti, file unmapped, containers e custom folders)
    globalSearch: (
        query: string,
    ): Array<{ type: "document" | "unmapped_file" | "container" | "custom_folder"; item: DocumentFile | UnmappedFile | Container | CustomFolder }> => {
        const searchTerm = query.toLowerCase();
        const results: Array<{
            type: "document" | "unmapped_file" | "container" | "custom_folder";
            item: DocumentFile | UnmappedFile | Container | CustomFolder;
        }> = [];

        // Cerca nei documenti
        documents.forEach((doc) => {
            if (
                doc.name.toLowerCase().includes(searchTerm) ||
                doc.type.toLowerCase().includes(searchTerm) ||
                Object.values(doc.extractedData).some((value) => String(value).toLowerCase().includes(searchTerm))
            ) {
                results.push({ type: "document", item: doc });
            }
        });

        // Cerca nei file unmapped
        unmappedFiles.forEach((file) => {
            if (
                file.name.toLowerCase().includes(searchTerm) ||
                file.type.toLowerCase().includes(searchTerm) ||
                (file.extractedData && Object.values(file.extractedData).some((value) => String(value).toLowerCase().includes(searchTerm)))
            ) {
                results.push({ type: "unmapped_file", item: file });
            }
        });

        // Cerca nei containers
        containerService.getAll().forEach((container) => {
            if (
                container.containerNumber.toLowerCase().includes(searchTerm) ||
                container.route.from.toLowerCase().includes(searchTerm) ||
                container.route.to.toLowerCase().includes(searchTerm)
            ) {
                results.push({ type: "container", item: container });
            }
        });

        // Cerca nelle custom folders
        customFolderService.getAll().forEach((folder) => {
            if (folder.name.toLowerCase().includes(searchTerm)) {
                results.push({ type: "custom_folder", item: folder });
            }
        });

        return results;
    },
};
